'use client'

import { useEffect, useMemo, useRef } from 'react'

import {
	buildGa4CartItem,
	priceOldFromPayableAndSavings,
	pushEvent,
	resolveItemCategory2ForCartProduct
} from '@/_utils/analytics/datalayer'
import {
	getGa4LastAmbulanceName,
	getGa4LastAmbulanceSlug,
	saveGa4LastAmbulanceSlug
} from '@/_utils/analytics/ga4-ambulance-slug.storage'
import { useGetAmbulance } from '@/app/[locale]/order/_services/useGetAmbulance'
import { useGetPreventiveExaminationCategory } from '@/app/[locale]/order/_services/useGetPreventiveExaminationCategory'
import { saveGa4PendingFamilyPurchase } from '@/app/[locale]/order/_utils/payment-utils'
import { cartProductKey } from '@cart/_utils/cart-product-key'

import { CartProduct, CartProductType, useOrderFlow } from '../../../../_context/order-flow-context'

import type { ApiResponse, IOrderTimeSlotDetail } from '@/_types'
import type { PriceInfo } from '@cart/_hooks/use-cart-prices'

type UseBeginCheckoutAnalyticsParams = {
	cartProducts: CartProduct[]
	productPrices: Record<string, PriceInfo>
	pricesLoaded: boolean
	totalAll: number
	primaryType: CartProductType | undefined
	annualProgramId: number
	yearProgramResponse: { data?: { categoryId?: number } } | undefined
	timeslotDetail: ApiResponse<IOrderTimeSlotDetail> | undefined
}

function cartNeedsAmbulanceCategory2(products: CartProduct[]): boolean {
	return products.some(
		p =>
			p.type === CartProductType.TEMPLATE || p.type === CartProductType.PRICE_LIST_ITEM
	)
}

/**
 * Fires GA4 `begin_checkout` once when the patient form is mounted (step 3 / Cesta B direct)
 * and cart line prices + § 4.4 category context are ready.
 */
export function useBeginCheckoutAnalytics({
	cartProducts,
	productPrices,
	pricesLoaded,
	totalAll,
	primaryType,
	annualProgramId,
	yearProgramResponse,
	timeslotDetail: _timeslotDetail
}: UseBeginCheckoutAnalyticsParams) {
	const beginCheckoutFiredRef = useRef(false)
	const { selectedAmbulanceSlug } = useOrderFlow()

	const ga4AmbulanceSlug = getGa4LastAmbulanceSlug() ?? selectedAmbulanceSlug
	const storedAmbulanceName = getGa4LastAmbulanceName()

	const preventiveProgramId =
		primaryType === CartProductType.PREVENTIVE_EXAM && cartProducts[0]?.type === CartProductType.PREVENTIVE_EXAM
			? cartProducts[0].data.programId
			: 0

	const needsPreventiveCategory = preventiveProgramId > 0
	const { data: zenyCategory } = useGetPreventiveExaminationCategory('zeny', needsPreventiveCategory)
	const { data: muziCategory } = useGetPreventiveExaminationCategory('muzi', needsPreventiveCategory)

	const preventiveCategorySlug = useMemo((): 'zeny' | 'muzi' | undefined => {
		if (!needsPreventiveCategory) return undefined
		if (
			zenyCategory?.data?.preventiveExaminations?.some(e => e.id === preventiveProgramId)
		) {
			return 'zeny'
		}
		if (
			muziCategory?.data?.preventiveExaminations?.some(e => e.id === preventiveProgramId)
		) {
			return 'muzi'
		}
		return undefined
	}, [needsPreventiveCategory, preventiveProgramId, zenyCategory?.data, muziCategory?.data])

	const needsAmbulanceCategory = cartNeedsAmbulanceCategory2(cartProducts)
	const shouldFetchAmbulanceName = needsAmbulanceCategory && !storedAmbulanceName && !!ga4AmbulanceSlug
	const { data: ambulanceResponse, isPending: isAmbulancePending } = useGetAmbulance(
		shouldFetchAmbulanceName ? ga4AmbulanceSlug : null
	)

	const ambulanceName =
		storedAmbulanceName?.trim() || ambulanceResponse?.data?.name?.trim() || undefined

	const analyticsContext = useMemo(
		() => ({
			yearProgramCategoryId: yearProgramResponse?.data?.categoryId,
			preventiveCategorySlug,
			ambulanceName
		}),
		[yearProgramResponse?.data?.categoryId, preventiveCategorySlug, ambulanceName]
	)

	useEffect(() => {
		if (beginCheckoutFiredRef.current || !pricesLoaded || cartProducts.length === 0) return

		const needsYearProgram =
			primaryType === CartProductType.ANNUAL_PROGRAM ||
			primaryType === CartProductType.ANNUAL_PROGRAM_FAMILY
		if (needsYearProgram && annualProgramId > 0 && !yearProgramResponse?.data) return

		if (needsPreventiveCategory && !preventiveCategorySlug) return

		if (needsAmbulanceCategory) {
			if (!ga4AmbulanceSlug) return
			if (!ambulanceName) {
				if (shouldFetchAmbulanceName && isAmbulancePending) return
				return
			}
		}

		beginCheckoutFiredRef.current = true

		if (ga4AmbulanceSlug) {
			saveGa4LastAmbulanceSlug(ga4AmbulanceSlug, ambulanceName)
		}

		const isFamily = cartProducts.every(p => p.type === CartProductType.ANNUAL_PROGRAM_FAMILY)
		const items = cartProducts.map((product, idx) => {
			const key = cartProductKey(product, idx)
			const priceInfo = productPrices[key]

			const item_category2 = resolveItemCategory2ForCartProduct(
				product.type,
				analyticsContext
			)

			const payable = priceInfo?.total ?? 0
			const priceOld = priceOldFromPayableAndSavings(payable, priceInfo?.savings ?? 0)

			const itemOverrides = {
				...(item_category2 !== undefined && { item_category2 }),
				index: idx
			}

			if (product.type === CartProductType.TEMPLATE) {
				return buildGa4CartItem(
					{
						type: product.type,
						templateId: product.data.templateId,
						timeslotId: product.data.timeslotId,
						name: priceInfo?.name ?? '',
						price: payable,
						...(priceOld !== undefined && { priceOld })
					},
					itemOverrides
				)
			}

			if (product.type === CartProductType.PRICE_LIST_ITEM) {
				return buildGa4CartItem(
					{
						type: product.type,
						itemId: product.data.itemId,
						name: priceInfo?.name ?? '',
						price: payable,
						...(priceOld !== undefined && { priceOld })
					},
					itemOverrides
				)
			}

			return buildGa4CartItem(
				{
					type: product.type,
					programId: product.data.programId,
					name: priceInfo?.name ?? '',
					price: payable,
					...(priceOld !== undefined && { priceOld })
				},
				itemOverrides
			)
		})

		pushEvent('begin_checkout', {
			currency: 'EUR',
			value: totalAll,
			...(isFamily && { coupon: 'rodina-10' }),
			items
		})

		if (isFamily) {
			saveGa4PendingFamilyPurchase({
				items,
				value: totalAll,
				coupon: 'rodina-10'
			})
		}
	}, [
		pricesLoaded,
		yearProgramResponse?.data,
		preventiveCategorySlug,
		analyticsContext,
		ambulanceName,
		ga4AmbulanceSlug,
		needsAmbulanceCategory,
		shouldFetchAmbulanceName,
		isAmbulancePending,
		cartProducts,
		productPrices,
		totalAll,
		primaryType,
		annualProgramId,
		needsPreventiveCategory
	])
}
